import { createClient, type Session } from "@supabase/supabase-js";

/** Legacy app auth paths redirect to the canonical pages on chartoon.co. */
export const LOGIN_PATH = "/dashboard/login";
export const SIGNUP_PATH = "/dashboard/signup";
export const RESET_PATH = "/dashboard/reset-password";
export const DASHBOARD_PATH = "/dashboard";
export const MIN_PASSWORD_LENGTH = 8;
export const AUTH_SITE_URL =
  import.meta.env.VITE_AUTH_SITE_URL ||
  (import.meta.env.DEV ? "http://localhost:3000" : "https://chartoon.co");
export const PASSWORD_RESET_URL = `${AUTH_SITE_URL}/reset-password`;

export function validateNewPassword(
  password: string,
  confirmation: string,
): string {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (password !== confirmation) {
    return "Passwords do not match.";
  }
  return "";
}

export function signInErrorMessage(message: string): string {
  return /invalid login credentials/i.test(message)
    ? "Email or password is incorrect."
    : message;
}

/** Return an auth error from either an OAuth query string or URL fragment. */
export function getAuthError(url = window.location.href): string {
  const parsed = new URL(url);
  const query = parsed.searchParams;
  const hash = new URLSearchParams(parsed.hash.replace(/^#/, ""));
  return query.get("error_description") || hash.get("error_description") || "";
}

export function isPasswordRecoveryCallback(
  url = window.location.href,
): boolean {
  const parsed = new URL(url);
  return (
    new URLSearchParams(parsed.hash.replace(/^#/, "")).get("type") ===
    "recovery"
  );
}

// Capture this before supabase-js consumes the callback fragment.
export const IS_PASSWORD_RECOVERY =
  typeof window !== "undefined" && isPasswordRecoveryCallback();

export function redirectRecoveryToWeb(session: Session) {
  const fragment = new URLSearchParams({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    expires_in: String(session.expires_in || 3600),
    token_type: session.token_type || "bearer",
    type: "recovery",
  });
  window.location.replace(`${PASSWORD_RESET_URL}#${fragment.toString()}`);
}

/** Bounce to login while preserving any provider error for the form. */
export function redirectToLogin() {
  const error = getAuthError();
  window.location.href = error
    ? `${AUTH_SITE_URL}/login?error=${encodeURIComponent(error)}`
    : `${AUTH_SITE_URL}/login`;
}

/**
 * Single shared browser client for the whole app (dashboard + canvas).
 * `detectSessionInUrl` consumes the magic-link hash on load; the session is
 * persisted in localStorage and the access token auto-refreshed.
 */
// Dev fallback: without Supabase env vars, createClient throws at import time
// and blanks the whole app. Placeholders keep it booting with no session —
// tokenless requests are accepted by the API's DEV_AUTH fallback.
// In a production build the fallback must never ship: a CI env drift would
// silently point auth at localhost. Fail loud instead.
if (import.meta.env.PROD && !import.meta.env.VITE_SUPABASE_URL) {
  throw new Error("VITE_SUPABASE_URL missing from production build");
}
export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL || "http://localhost:54321",
  import.meta.env.VITE_SUPABASE_ANON_KEY || "dev-anon-key",
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  },
);

export async function signOut() {
  await supabase.auth.signOut();
  window.location.href = `${AUTH_SITE_URL}/login`;
}
